import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// JWT decode utility with error handling
export const decodeJWT = (token) => {
  try {
    return jwtDecode(token);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

// Check if token is expired
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded) return true;
  
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

// Check if user has admin role (excluding managers)
export const isAdmin = (decodedToken) => {
  return (decodedToken?.type === 'admin' || decodedToken?.role === 'admin') && 
         decodedToken?.type !== 'manager' && decodedToken?.role !== 'manager';
};

// Check if user has manager role
export const isManager = (decodedToken) => {
  return decodedToken?.type === 'manager' || decodedToken?.role === 'manager';
};

// Check if user is LINE authenticated
export const isLineUser = (decodedToken) => {
  return decodedToken?.type === 'user'|| decodedToken?.lineUserId;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Authentication middleware - validates token and sets user state (Hybrid approach)
  const validateAndSetUser = async (jwtToken) => {
    if (!jwtToken) {
      setLoading(false);
      return;
    }

    // Step 1: Client-side JWT validation (fast, primary method)
    if (isTokenExpired(jwtToken)) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      toast.error('Session expired. Please login again.');
      return;
    }

    // Decode token to get user info
    const decoded = decodeJWT(jwtToken);
    if (!decoded) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      toast.error('Invalid token. Please login again.');
      return;
    }

    // Step 2: Set user from JWT immediately (for fast UI)
    const userFromJWT = {
      ...decoded,
      isAdmin: isAdmin(decoded),
      isLineUser: isLineUser(decoded),
      isManager: isManager(decoded)
    };

    setUser(userFromJWT);
    setIsAuthenticated(true);
    setToken(jwtToken);
    localStorage.setItem('token', jwtToken);

    // Step 3: Server-side validation (secondary, for security and fresh data)
    try {
      const response = await authAPI.validateToken(jwtToken);
      const userData = response.data.user;
      
      // Merge fresh backend user data with JWT claims
      const userWithFreshData = {
        ...userData,
        ...decoded,
        isAdmin: isAdmin(decoded),
        isLineUser: isLineUser(decoded),
        isManager: isManager(decoded)
      };

      setUser(userWithFreshData);
    } catch (error) {
      console.error('Server-side token validation failed:', error);
      
      // Handle different validation failure scenarios
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Security issue - immediate logout
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        
        const message = error.response?.data?.message || 'Session invalid. Please login again.';
        toast.error(message);
        
        // Redirect to login page
        window.location.href = '/#/login';
      } else if (error.code === 'ECONNABORTED' || !error.response) {
        // Network issue - graceful degradation, keep JWT user data
        console.warn('Token validation failed due to network issue, keeping current session with JWT data');
        // Continue with userFromJWT that was already set
      } else {
        // Other errors - remove token for safety
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        toast.error('Authentication error. Please login again.');
      }
    }
    
    setLoading(false);
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        await validateAndSetUser(storedToken);
      } else {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const { token: jwtToken, user: userData } = response.data;
      
      await validateAndSetUser(jwtToken);
      
      toast.success(`Welcome back, ${userData.name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed. Please check your credentials.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const loginWithLine = async (lineProfile) => {
    try {
      const response = await authAPI.loginWithLine(lineProfile);
      const { token: jwtToken, user: userData } = response.data;
      
      await validateAndSetUser(jwtToken);
      
      toast.success(`Welcome, ${lineProfile.displayName}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'LINE login failed.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const registerLineUser = async (lineProfile, userData) => {
    try {
      const response = await authAPI.registerLineUser({
        ...lineProfile,
        ...userData
      });
      const { token: jwtToken, user: newUser } = response.data;
      
      await validateAndSetUser(jwtToken);
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed.';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await authAPI.logout();
      }
    } catch (e) {
      console.error('Logout api failed', e);
    }
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  // Middleware function to check authentication before navigation
  const requireAuth = () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken || isTokenExpired(currentToken)) {
      return false;
    }
    return true;
  };

  // Middleware to check admin role
  const requireAdmin = () => {
    if (!requireAuth()) return false;
    const decoded = decodeJWT(token);
    return isAdmin(decoded);
  };

  // Middleware to check LINE user (including managers who act as line users)
  const requireLineUser = () => {
    if (!requireAuth()) return false;
    const decoded = decodeJWT(token);
    return isLineUser(decoded) || isManager(decoded);
  };

  // Middleware to check manager role
  const requireManager = () => {
    if (!requireAuth()) return false;
    const decoded = decodeJWT(token);
    return isManager(decoded);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    token,
    login,
    loginWithLine,
    registerLineUser,
    logout,
    requireAuth,
    requireAdmin,
    requireLineUser,
    requireManager,
    isAdmin,
    isLineUser,
    isManager,
    validateAndSetUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
